import { Dispatch, SetStateAction, useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { Button, Spinner } from "@blueprintjs/core";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";

import { AppToaster } from "@components";
import { OrgContext } from "@providers";

import { subscriptionSignup } from "../../api";

const CardWrapper = styled.div`
  max-width: 480px;
`;

type Props = {
  priceId: string;
  useSpinner: boolean;
  setSpinner: Dispatch<SetStateAction<boolean>>;
  quantity: number;
};

export const CheckoutForm: React.FC<Props> = ({
  priceId,
  useSpinner,
  setSpinner,
  quantity,
}) => {
  const history = useHistory();
  const stripe = useStripe();
  const elements = useElements();
  const { org } = useContext(OrgContext);
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const orgId = org?.id;

  const handleSubmit = async (event: any) => {
    // Block native form submission.
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      AppToaster.show({
        message: `Stripe.js has not finished loading`,
        intent: "danger",
      });
      return;
    }

    if (!orgId) {
      AppToaster.show({
        message: `Cannot add subscription without orgId`,
        intent: "danger",
      });
      return;
    }

    // Get a reference to a mounted CardElement. Elements knows how
    // to find your CardElement because there can only ever be one of
    // each type of element.
    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      AppToaster.show({
        message: `No Card Element available`,
        intent: "danger",
      });
      return;
    }
    setSpinner(true);
    try {
      const response = await subscriptionSignup(orgId, priceId, quantity);
      const payload = await stripe.confirmCardPayment(response.client_secret, {
        payment_method: {
          card: cardElement,
        },
      });
      if (payload.error) {
        AppToaster.show({
          message: `Error: ${payload.error.message}`,
          intent: "danger",
        });
      } else {
        history.go(0);
      }
    } catch {
      AppToaster.show({
        message: `Payment failed: please contact support@r2c.dev for assistance`,
        intent: "danger",
      });
    }
    setSpinner(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <CardWrapper>
        <CardElement
          onChange={({ complete }) => setIsComplete(complete)}
          options={{
            style: {
              base: {
                fontSize: "16px",
                color: "#424770",
                "::placeholder": {
                  color: "#aab7c4",
                },
              },
              invalid: {
                color: "#9e2146",
              },
            },
          }}
        />
      </CardWrapper>
      <br />
      {useSpinner ? (
        <Spinner intent="primary" />
      ) : (
        <Button type="submit" intent="primary" disabled={!isComplete}>
          Upgrade to Team
        </Button>
      )}
    </form>
  );
};
